import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, Output, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AppPreferencesService } from '../../../services/app-preferences';
import { Commonservice } from '../../../services/commonservice';
import { SessionService } from '../../../services/session.service';
import { ServerTimeService } from '../../../services/server-time';
import { DashboardHeaderUser } from './header.interfaces';

@Component({
  selector: 'app-dashboard-header',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class DashboardHeaderComponent implements AfterViewInit, OnDestroy {
  @Input() title = 'Nepal Load Dispatch Center';
  @Input() subtitle = 'National Power Grid Management';
  @Input({ required: true }) user!: DashboardHeaderUser;
  @Input() currentTime = '';

  @Output() menuToggle = new EventEmitter<void>();
  @HostBinding('class.header--scrolled') protected headerScrolled = false;
  @HostBinding('style.--header-sticky-offset.px') protected headerStickyOffset = 0;

  protected showProfileMenu = false;

  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private scrollContainer: HTMLElement | null = null;
  private readonly scrollListener = (): void => this.updateStickyState();
  private readonly resizeListener = (): void => this.syncStickyOffset();

  protected get displayUser(): DashboardHeaderUser {
    const sessionProfile = this.sessionSvc.getProfileDetails();
    const sessionFullName = sessionProfile?.user_fullname?.trim() || this.user.fullName;
    const profileImageUrl = sessionProfile?.profile_image?.uploaded_file || this.user.profileImageUrl || null;
    const roleLabel = sessionProfile?.role_name?.trim() || this.user.roleLabel;

    return {
      ...this.user,
      fullName: sessionFullName,
      initials: this.buildInitials(sessionFullName),
      roleLabel,
      profileImageUrl,
    };
  }

  protected get currentTimeLabel(): string {
    return this.serverTimeSvc.getCurrentTimeLabel();
  }

  protected get darkModeEnabled(): boolean {
    return this.preferencesSvc.darkMode;
  }

  protected get showBsDateEnabled(): boolean {
    return this.preferencesSvc.showBsDate;
  }

  protected handleMenuToggle(): void {
    this.menuToggle.emit();
  }

  protected toggleProfileMenu(): void {
    this.showProfileMenu = !this.showProfileMenu;
  }

  protected toggleDarkMode(): void {
    this.preferencesSvc.setDarkMode(!this.preferencesSvc.darkMode);
  }

  protected toggleShowBsDate(enabled?: boolean): void {
    this.preferencesSvc.setShowBsDate(typeof enabled === 'boolean' ? enabled : !this.preferencesSvc.showBsDate);
  }

  protected handleMyProfile(): void {
    this.showProfileMenu = false;
    this.router.navigate(['/dashboard/my-profile']);
  }

  protected handleSettings(): void {
    this.showProfileMenu = false;
    this.router.navigate(['/dashboard/settings']);
  }

  constructor(
    private readonly commonSvc: Commonservice,
    private readonly router: Router,
    private readonly sessionSvc: SessionService,
    private readonly serverTimeSvc: ServerTimeService,
    private readonly preferencesSvc: AppPreferencesService,
  ) {}

  ngAfterViewInit(): void {
    this.scrollContainer = this.findScrollableParent();
    this.syncStickyOffset();
    this.updateStickyState();

    if (this.scrollContainer instanceof HTMLElement) {
      this.scrollContainer.addEventListener('scroll', this.scrollListener, { passive: true });
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.resizeListener, { passive: true });
    }
  }

  ngOnDestroy(): void {
    if (this.scrollContainer instanceof HTMLElement) {
      this.scrollContainer.removeEventListener('scroll', this.scrollListener);
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  protected async handleLogout(): Promise<void> {
    this.showProfileMenu = false;
    await this.commonSvc.logout();
    this.router.navigate(['/signin']);
  }

  private buildInitials(fullName: string): string {
    const initials = fullName
      .split(' ')
      .map((part) => part.trim())
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');

    return initials || this.user.initials;
  }

  private findScrollableParent(): HTMLElement | null {
    if (typeof window === 'undefined') {
      return null;
    }

    let parent = this.hostElement.nativeElement.parentElement;
    while (parent) {
      const styles = window.getComputedStyle(parent);
      const isScrollable = /(auto|scroll|overlay)/.test(styles.overflowY) && parent.scrollHeight > parent.clientHeight;
      if (isScrollable) {
        return parent;
      }
      parent = parent.parentElement;
    }

    return null;
  }

  private syncStickyOffset(): void {
    const host = this.hostElement.nativeElement;
    if (this.scrollContainer instanceof HTMLElement) {
      const containerRect = this.scrollContainer.getBoundingClientRect();
      const hostRect = host.getBoundingClientRect();
      this.headerStickyOffset = Math.max(0, Math.round(hostRect.top - containerRect.top));
      return;
    }

    this.headerStickyOffset = Math.max(0, Math.round(host.getBoundingClientRect().top));
  }

  private updateStickyState(): void {
    if (this.scrollContainer instanceof HTMLElement) {
      this.headerScrolled = this.scrollContainer.scrollTop > 0;
      return;
    }
    this.headerScrolled = false;
  }
}
